import Modal2 from 'components/commons/modal/Modal2';
import React from 'react';
import ReactConfetti from 'react-confetti';
import { commaFormat } from 'components/features/new-report/utils/formatter';

const Confetti = (): React.ReactElement => {
  return (
    <>
      <ReactConfetti
        {...{
          width: window.innerWidth,
          height: window.innerHeight,
          style: { zIndex: 9999 },
          gravity: 0.2,
          numberOfPieces: 1500,
          initialVelocityX: 13,
          initialVelocityY: 50,
          wind: 0,
          opacity: 70,
          recycle: false,
        }}
      />
    </>
  );
};

export const TargetAchievementRateModal = ({
  hide,
  isShow,
  title,
  startDate,
  targetAmount,
  accumulationAmount,
  percentage,
}: {
  isShow: boolean;
  hide: () => void;
  title: string;
  startDate: Date;
  targetAmount: number;
  accumulationAmount: number;
  percentage: number;
}): React.ReactElement => {
  const progressInfo: {
    progress: number;
    img: string;
    comment: string;
  } = (() => {
    if (percentage < 19) {
      return {
        progress: 10,
        img: 'achievement_10',
        comment: '着実に資産を増やしていきましょう',
      };
    }
    if (percentage < 29) {
      return {
        progress: 20,
        img: 'achievement_20',
        comment: '着実に資産を増やしていきましょう',
      };
    }
    if (percentage < 39) {
      return {
        progress: 30,
        img: 'achievement_30',
        comment: '順調に目標に近づいています',
      };
    }
    if (percentage < 49) {
      return {
        progress: 40,
        img: 'achievement_40',
        comment: '順調に目標に近づいています',
      };
    }
    if (percentage < 59) {
      return {
        progress: 50,
        img: 'achievement_50',
        comment: '折り返し地点に到達しました',
      };
    }
    if (percentage < 69) {
      return {
        progress: 60,
        img: 'achievement_60',
        comment: '順調に目標に近づいています',
      };
    }
    if (percentage < 79) {
      return {
        progress: 70,
        img: 'achievement_70',
        comment: '順調に目標に近づいています',
      };
    }
    if (percentage < 89) {
      return {
        progress: 80,
        img: 'achievement_80',
        comment: '目標達成まであと一歩です！',
      };
    }
    return {
      progress: 90,
      img: 'achievement_90',
      comment: '目標達成まであと一歩です！',
    };
  })();

  const calculateAchievePeriod = ({
    startDate,
  }: {
    startDate: Date;
  }): {
    year: number;
    month: number;
  } => {
    const toMonthNumber = (date: Date) =>
      date.getFullYear() * 12 + date.getMonth();
    const remainMonths = toMonthNumber(new Date()) - toMonthNumber(startDate);
    return {
      year: Math.floor(remainMonths / 12),
      month: remainMonths % 12,
    };
  };
  const { year, month } = calculateAchievePeriod({ startDate });

  return (
    <>
      {isShow && <Confetti />}
      <Modal2 isShow={isShow}>
        <div className="p-4 max-h-modal overflow-y-auto">
          <div className="flex flex-row">
            <div
              className="icon-close ml-auto text-gray-500 cursor-pointer"
              onClick={hide}
            />
          </div>
          {percentage >= 100 ? (
            <>
              <div className="font-bold text-bluegray-700 text-lg text-center truncate">
                {title}の
              </div>
              <div className="font-bold text-red-500 text-2xl text-center text-red-550">
                目標達成しました！
              </div>
              <div className="mt-4">
                <div className="text-base font-bold text-bluegray-700 text-center">
                  最後までやり遂げることができましたね
                </div>
                <div className="text-base font-bold text-bluegray-700 text-center">
                  おめでとうございます！
                </div>
              </div>
              <div className="py-3 px-4 mt-4 border border-bluegray-300 rounded text-bluegray-700">
                <div className="flex flex-row">
                  <div className="text-sm">貯まった金額</div>
                  <div className="text-sm ml-auto">
                    <span className="text-base font-bold">
                      {commaFormat(accumulationAmount, { numberOfDecimals: 0 })}
                    </span>
                    円
                  </div>
                </div>
                <div className="mt-1 flex flex-row">
                  <div className="text-sm">かかった年月</div>
                  <div className="text-base ml-auto">
                    {!year && !month
                      ? '1ヶ月未満'
                      : `${year ? `${year}年` : ''}${
                          month ? `${month}ヶ月` : ''
                        }`}
                  </div>
                </div>
              </div>
              <img
                src="/static/images/advisor/banzai.svg"
                alt="バンザイ"
                className={`mt-4`}
              />
            </>
          ) : (
            <>
              <div className="flex flex-col text-red-550">
                <div className="font-bold text-bluegray-700 text-lg text-center">
                  目標金額達成率が
                </div>
                <div className="font-bold text-red-500 text-2xl text-center">
                  {progressInfo.progress}%を超えました！
                </div>
              </div>
              <div className="flex flex-row justify-center mt-4">
                <img
                  alt="目標達成率"
                  src={`/static/images/advisor/${progressInfo.img}.svg`}
                  className="mt-4 mx-2"
                />
              </div>
              <div className="mt-4 text-bluegray-700">
                <div className="text-base font-bold text-center">
                  {progressInfo.comment}
                </div>
                <div className="flex flex-row mx-5 mt-2">
                  <div className="text-sm">貯まった金額</div>
                  <div className="flex flex-row text-sm ml-auto">
                    <div className="text-base font-bold">
                      {commaFormat(accumulationAmount, { numberOfDecimals: 0 })}
                    </div>
                    <div>円</div>
                  </div>
                </div>
                <div className="flex flex-row mx-5">
                  <div className="text-sm">全体の目標金額</div>
                  <div className="flex flex-row text-sm ml-auto">
                    <div className="text-base">
                      {commaFormat(targetAmount, { numberOfDecimals: 0 })}
                    </div>
                    <div>円</div>
                  </div>
                </div>
              </div>
            </>
          )}
          <div className="m-4 flex flex-col">
            <button
              className="text-sm text-bluegray-500 py-2 flex flex-row items-center justify-center mt-2"
              onClick={hide}>
              閉じる
            </button>
          </div>
        </div>
      </Modal2>
    </>
  );
};
